<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PatientAddress, PatientWithoutAntecedentes } from '@/api/patients';
import { useTableFilter } from '@/composables/useTableFilter';
import { addressHasCoordinates } from './route-plan-setup.utils';

// Compact one-row picker: patient autocomplete plus a category narrower (only when the
// loaded patients carry service assignments). The category filter narrows the dropdown
// options only — it never removes an already-selected patient.
interface Props {
  patients: PatientWithoutAntecedentes[];
}

const props = defineProps<Props>();
const modelValue = defineModel<number[]>({ required: true });

const { t } = useI18n();
const nameFilter = useTableFilter(['fullName']);

// Value 0 is the "Sin categoría" bucket, same convention as the board's filter.
const UNCATEGORIZED_FILTER_VALUE = 0;
const patientCategoryIds = ref<number[]>([]);
const hasPatientCategories = computed(() =>
  props.patients.some(patient => patient.currentServiceAssignment));
const patientCategoryItems = computed(() => {
  const seen = new Map<number, string>();
  props.patients.forEach((patient) => {
    const assignment = patient.currentServiceAssignment;
    if (assignment) {
      seen.set(assignment.categoryId, assignment.categoryName);
    } else {
      seen.set(UNCATEGORIZED_FILTER_VALUE, t('routePlan.board.noCategory'));
    }
  });

  return [...seen.entries()].map(([value, title]) => ({ value, title }));
});

// Already-selected patients stay listed even when filtered out, so their chips keep
// rendering with full item data.
const selectablePatients = computed(() => {
  if (patientCategoryIds.value.length === 0) return props.patients;

  return props.patients.filter((patient) =>
    modelValue.value.includes(patient.id) ||
    patientCategoryIds.value.includes(
      patient.currentServiceAssignment?.categoryId ?? UNCATEGORIZED_FILTER_VALUE,
    ));
});

// Uses the address model, not the patient's legacy root latitude/longitude, so the chip
// agrees with the missing-address banner and the per-visit checks in the parent.
function patientHasCoordinates(patient: { patientAddressesAttributes?: PatientAddress[] }): boolean {
  return addressHasCoordinates(patient.patientAddressesAttributes ?? [], null);
}

function patientItemSubtitle(patient: PatientWithoutAntecedentes): string | undefined {
  if (!patientHasCoordinates(patient)) return t('routePlan.noCoordinates');

  return patient.currentServiceAssignment?.categoryName ?? undefined;
}
</script>

<template>
  <v-row
    dense
    class="mb-4"
  >
    <v-col
      cols="12"
      :sm="hasPatientCategories ? 8 : 12"
    >
      <v-autocomplete
        v-model="modelValue"
        :items="selectablePatients"
        item-title="fullName"
        item-value="id"
        :custom-filter="nameFilter"
        :label="t('routePlan.selectPatients')"
        variant="outlined"
        density="compact"
        multiple
        chips
        closable-chips
        hide-details
      >
        <template #chip="{ props: chipProps, item }">
          <v-chip
            v-bind="chipProps"
            :prepend-icon="patientHasCoordinates(item.raw) ? undefined : 'mdi-map-marker-off'"
            :color="patientHasCoordinates(item.raw) ? undefined : 'warning'"
          >
            {{ item.raw.fullName }}
          </v-chip>
        </template>
        <template #item="{ props: itemProps, item }">
          <v-list-item
            v-bind="itemProps"
            :subtitle="patientItemSubtitle(item.raw)"
          >
            <template
              v-if="!patientHasCoordinates(item.raw)"
              #append
            >
              <v-icon color="warning">
                mdi-map-marker-off
              </v-icon>
            </template>
          </v-list-item>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col
      v-if="hasPatientCategories"
      cols="12"
      sm="4"
    >
      <v-select
        v-model="patientCategoryIds"
        :items="patientCategoryItems"
        :label="t('routePlan.board.categoryFilter')"
        variant="outlined"
        density="compact"
        multiple
        clearable
        hide-details
      />
    </v-col>
  </v-row>
</template>
